import type { ToolCatalog } from "../api/admin-client.ts"

/**
 * docs 카테고리는 `/mcp/docs` 전역 공개 엔드포인트로 이미 접근 가능하므로 서비스별 MCP 서버의
 * Enabled Tools 매트릭스에서는 노출하지 않는다. PublicDocsMcpCard 가 공개 URL 을 안내한다.
 */
const HIDDEN_CATEGORIES: ReadonlySet<string> = new Set(["docs"])

/**
 * 툴 체크박스 매트릭스 — `TOOL_CATALOG` 를 카테고리(knowledge/episodes) 별로 그룹핑.
 *
 * 값: enabled 툴 이름 배열. 카탈로그가 비면 빈 상태를 표시한다.
 * `docs` 카테고리는 공개 MCP 엔드포인트 존재로 제외.
 */
export function ToolMatrix(props: {
	readonly catalog: ToolCatalog | undefined
	readonly value: ReadonlyArray<string>
	readonly onChange: (next: ReadonlyArray<string>) => void
}) {
	if (!props.catalog) {
		return <div className="muted">카탈로그 로딩 중...</div>
	}

	const byCategory = groupByCategory(props.catalog.catalog)
	const categories = Object.keys(byCategory)
		.filter((c) => !HIDDEN_CATEGORIES.has(c))
		.sort()

	function toggle(tool: string) {
		if (props.value.includes(tool)) {
			props.onChange(props.value.filter((t) => t !== tool))
		} else {
			props.onChange([...props.value, tool])
		}
	}

	function toggleCategory(category: string) {
		const tools = byCategory[category] ?? []
		const allSelected = tools.every((t) => props.value.includes(t))
		if (allSelected) {
			props.onChange(props.value.filter((v) => !tools.includes(v)))
		} else {
			const next = new Set(props.value)
			for (const t of tools) next.add(t)
			props.onChange(Array.from(next))
		}
	}

	return (
		<div className="tool-matrix">
			{categories.map((category) => {
				const tools = byCategory[category] ?? []
				const selected = tools.filter((t) => props.value.includes(t)).length
				const allSelected = tools.length > 0 && selected === tools.length
				const partial = selected > 0 && !allSelected
				return (
					<div key={category} className="tool-category">
						<div className="tool-category-head">
							<div className="tool-category-title">
								<span>{category}</span>
								<span className="tag">
									{selected}/{tools.length}
								</span>
							</div>
							<label className="tool-category-all">
								<input
									type="checkbox"
									className="pk-check"
									checked={allSelected}
									ref={(el) => {
										if (el) el.indeterminate = partial
									}}
									onChange={() => toggleCategory(category)}
								/>
								전체
							</label>
						</div>
						<div className="tool-rows">
							{tools.map((tool) => {
								const spec = props.catalog?.catalog[tool]
								return (
									<label key={tool} className="tool-row">
										<input
											type="checkbox"
											className="pk-check"
											checked={props.value.includes(tool)}
											onChange={() => toggle(tool)}
										/>
										<div className="tool-row-text">
											<span className="tool-name">{tool}</span>
											{spec && spec.requiredScopes.length > 0 && (
												<div className="tool-meta">
													{spec.requiredScopes.map((s) => (
														<span key={s} className="tool-scope-chip">
															{s}
														</span>
													))}
												</div>
											)}
										</div>
									</label>
								)
							})}
						</div>
					</div>
				)
			})}
		</div>
	)
}

function groupByCategory(catalog: ToolCatalog["catalog"]): Record<string, ReadonlyArray<string>> {
	const out: Record<string, Array<string>> = {}
	for (const [tool, spec] of Object.entries(catalog)) {
		const bucket = out[spec.category] ?? []
		bucket.push(tool)
		out[spec.category] = bucket
	}
	for (const key of Object.keys(out)) out[key]?.sort()
	return out
}
